import { useState, useEffect, useRef } from "react";
import { MusicNote, Pause, X, Playlist, PencilSimple } from "@phosphor-icons/react";
import useDraggableFAB from "@/hooks/useDraggableFAB";

/**
 * MusicPlayer — floating YouTube widget with autoplay + loop.
 * Browsers block autoplay until user clicks the button (1 user gesture).
 * Features:
 *  - Default 10-song playlist (admin-editable via localStorage 'dyd_music_playlist')
 *  - Custom URL input
 *  - Persists current track via localStorage 'dyd_music_video_id'
 */
const DEFAULT_PLAYLIST = [
  { id: "mm2rjTCRZ-Y", title: "DYD · Track principal" },
  { id: "dQw4w9WgXcQ", title: "Energía deportiva 1" },
  { id: "btPJPFnesV4", title: "Eye of the Tiger · Survivor" },
  { id: "04854XqcfCY", title: "We Will Rock You · Queen" },
  { id: "1k8craCGpgs", title: "Don't Stop Me Now · Queen" },
  { id: "fJ9rUzIMcZQ", title: "Bohemian Rhapsody · Queen" },
  { id: "QkF3oxziUI4", title: "Stronger · Kanye West" },
  { id: "y6120QOlsfU", title: "Sandstorm · Darude" },
  { id: "60ItHLz5WEA", title: "Faded · Alan Walker" },
  { id: "9bZkp7q19f0", title: "Gangnam Style · PSY" },
];

const PLAYLIST_KEY = "dyd_music_playlist";
const VIDEO_ID_KEY = "dyd_music_video_id";

const loadPlaylist = () => {
  try {
    const raw = localStorage.getItem(PLAYLIST_KEY);
    if (raw) {
      const arr = JSON.parse(raw);
      if (Array.isArray(arr) && arr.length > 0) return arr;
    }
  } catch { /* ignore */ }
  return DEFAULT_PLAYLIST;
};

const parseYouTubeId = (input) => {
  const s = (input || "").trim();
  if (!s) return null;
  const match = s.match(/(?:v=|youtu\.be\/|embed\/|shorts\/)([A-Za-z0-9_-]{11})/) || s.match(/^([A-Za-z0-9_-]{11})$/);
  const id = match ? match[1] : s;
  return id && /^[A-Za-z0-9_-]{11}$/.test(id) ? id : null;
};

export default function MusicPlayer({ isAdmin = false }) {
  const [playing, setPlaying] = useState(false);
  const [collapsed, setCollapsed] = useState(true);
  const [editing, setEditing] = useState(false); // quick URL edit
  const [managing, setManaging] = useState(false); // admin playlist manager
  const [urlInput, setUrlInput] = useState("");
  const [playlist, setPlaylist] = useState(loadPlaylist);
  const [videoId, setVideoId] = useState(() => {
    try { return localStorage.getItem(VIDEO_ID_KEY) || playlist[0]?.id || DEFAULT_PLAYLIST[0].id; } catch { return DEFAULT_PLAYLIST[0].id; }
  });
  const iframeRef = useRef(null);
  // Mobile: raise above bottom nav + install banner + mascot. Desktop keeps existing offset.
  const isMobile = typeof window !== "undefined" && window.innerWidth < 1024;
  const { style: fabStyle, bind: fabBind, dragging: fabDragging } = useDraggableFAB("music", { right: 16, bottom: isMobile ? 258 : 168 });

  useEffect(() => {
    try {
      if (sessionStorage.getItem("dyd_music_playing") === "1") { setPlaying(true); setCollapsed(false); }
    } catch { /* ignore */ }
  }, []);
  useEffect(() => {
    try { sessionStorage.setItem("dyd_music_playing", playing ? "1" : "0"); } catch { /* ignore */ }
  }, [playing]);

  const persistPlaylist = (next) => {
    setPlaylist(next);
    try { localStorage.setItem(PLAYLIST_KEY, JSON.stringify(next)); } catch { /* ignore */ }
  };

  const toggle = () => {
    if (!playing) { setPlaying(true); setCollapsed(false); }
    else { setPlaying(false); }
  };

  const playTrack = (id) => {
    setVideoId(id);
    try { localStorage.setItem(VIDEO_ID_KEY, id); } catch { /* ignore */ }
    if (!playing) setPlaying(true);
  };

  const applyUrl = () => {
    const id = parseYouTubeId(urlInput);
    if (!id) { alert("URL o ID inválido. Pega un enlace válido de YouTube."); return; }
    playTrack(id);
    setEditing(false);
    setUrlInput("");
  };

  const updatePlaylistItem = (idx, field, value) => {
    const next = playlist.map((t, i) => (i === idx ? { ...t, [field]: value } : t));
    setPlaylist(next);
  };

  const savePlaylistItem = (idx) => {
    const t = playlist[idx];
    const id = parseYouTubeId(t.id);
    if (!id) { alert(`Pista ${idx + 1}: URL/ID inválido`); return; }
    const cleaned = { id, title: (t.title || "").slice(0, 60) || `Track ${idx + 1}` };
    const next = playlist.map((x, i) => (i === idx ? cleaned : x));
    persistPlaylist(next);
  };

  const resetPlaylist = () => {
    if (!window.confirm("¿Restaurar la lista por defecto?")) return;
    persistPlaylist(DEFAULT_PLAYLIST);
  };

  if (collapsed) {
    return (
      <button
        onClick={(e) => { if (fabDragging) { e.preventDefault(); return; } toggle(); }}
        {...fabBind}
        style={fabStyle}
        className={`z-50 w-12 h-12 rounded-full flex items-center justify-center shadow-lg border-2 transition-all hover:scale-110 select-none ${playing ? "bg-[#CCFF00] border-[#CCFF00] animate-pulse" : "bg-black border-[#CCFF00]/50"}`}
        aria-label={playing ? "Pausar música (arrastra para mover)" : "Activar música (arrastra para mover)"}
        data-testid="music-toggle-btn"
        title={playing ? "Pausar música" : "Activar música"}
      >
        <MusicNote size={22} weight="fill" className={playing ? "text-black" : "text-[#CCFF00]"} />
      </button>
    );
  }

  const currentTrack = playlist.find((t) => t.id === videoId);

  return (
    <div
      {...fabBind}
      style={{ ...fabStyle, cursor: fabDragging ? "grabbing" : "grab" }}
      className="z-50 bg-black border border-[#CCFF00]/40 shadow-2xl rounded-lg overflow-hidden w-80"
      data-testid="music-player-panel"
    >
      <div className="flex items-center justify-between px-2 py-1 bg-[#CCFF00]/10 border-b border-[#CCFF00]/20">
        <span className="text-[10px] tracking-widest uppercase font-bold text-[#CCFF00] flex items-center gap-1">
          <MusicNote size={12} weight="fill" /> Música DYD
        </span>
        <div className="flex items-center gap-1">
          {isAdmin && (
            <button onClick={() => { setManaging(!managing); setEditing(false); }} className={`text-[10px] px-1 ${managing ? "text-[#CCFF00]" : "text-gray-400 hover:text-[#CCFF00]"}`} title="Administrar playlist (admin)" data-testid="music-manage-btn">
              <PencilSimple size={12} weight="fill" />
            </button>
          )}
          <button onClick={() => { setEditing(!editing); setManaging(false); }} className={`text-[10px] px-1 ${editing ? "text-[#CCFF00]" : "text-gray-400 hover:text-[#CCFF00]"}`} title="Cambiar canción por URL" data-testid="music-edit-btn">✎</button>
          <button onClick={() => setPlaying(false)} className="text-[10px] text-gray-400 hover:text-red-400 px-1" title="Pausar" data-testid="music-pause-btn"><Pause size={12} /></button>
          <button onClick={() => setCollapsed(true)} className="text-[10px] text-gray-400 hover:text-white px-1" title="Minimizar" data-testid="music-collapse-btn"><X size={12} /></button>
        </div>
      </div>

      {/* Playlist selector — always visible */}
      <div className="p-2 bg-[#0A0A0A] border-b border-white/5 space-y-1.5">
        <div className="flex items-center gap-1.5">
          <Playlist size={12} weight="duotone" className="text-[#CCFF00]/70 shrink-0" />
          <span className="text-[9px] tracking-widest uppercase font-bold text-gray-400">Playlist (10)</span>
        </div>
        <select
          value={videoId}
          onChange={(e) => playTrack(e.target.value)}
          className="w-full bg-black border border-white/15 px-2 py-1.5 text-[11px] text-white focus:border-[#CCFF00]/50 focus:outline-none rounded"
          data-testid="music-playlist-select"
        >
          {playlist.map((t, i) => (
            <option key={`${t.id}-${i}`} value={t.id}>{i + 1}. {t.title}</option>
          ))}
          {currentTrack === undefined && videoId && (
            <option value={videoId}>Pista personalizada</option>
          )}
        </select>
      </div>

      {/* Quick URL edit panel */}
      {editing && (
        <div className="p-2 bg-[#0A0A0A] border-b border-[#CCFF00]/20 space-y-2" data-testid="music-edit-panel">
          <label className="text-[9px] tracking-widest uppercase font-bold text-gray-400 block">Pega URL o ID de YouTube</label>
          <input
            type="text"
            value={urlInput}
            onChange={(e) => setUrlInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") applyUrl(); if (e.key === "Escape") setEditing(false); }}
            placeholder="https://www.youtube.com/watch?v=..."
            autoFocus
            className="w-full bg-black border border-white/15 px-2 py-1.5 text-[11px] text-white placeholder:text-gray-600 focus:border-[#CCFF00]/50 focus:outline-none rounded"
            data-testid="music-url-input"
          />
          <div className="flex gap-1.5 justify-end">
            <button onClick={() => setEditing(false)} className="text-[10px] px-2 py-1 text-gray-400 hover:text-white border border-white/10 rounded" data-testid="music-url-cancel">Cancelar</button>
            <button onClick={applyUrl} className="text-[10px] px-2 py-1 bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase rounded" data-testid="music-url-apply">Aplicar</button>
          </div>
        </div>
      )}

      {/* Admin playlist manager panel */}
      {managing && isAdmin && (
        <div className="p-2 bg-[#0A0A0A] border-b border-amber-400/20 space-y-1.5 max-h-72 overflow-y-auto" data-testid="music-manage-panel">
          <div className="flex items-center justify-between">
            <span className="text-[9px] tracking-widest uppercase font-bold text-amber-300">Admin · Editar Playlist</span>
            <button onClick={resetPlaylist} className="text-[9px] uppercase tracking-wider text-gray-400 hover:text-red-400" data-testid="music-reset-playlist">Restaurar</button>
          </div>
          {playlist.map((t, i) => (
            <div key={`row-${i}`} className="grid grid-cols-[20px_1fr_auto] gap-1.5 items-center" data-testid={`music-track-row-${i}`}>
              <span className="text-[9px] text-gray-500 font-mono text-center">{i + 1}</span>
              <div className="flex flex-col gap-0.5">
                <input
                  value={t.title}
                  onChange={(e) => updatePlaylistItem(i, "title", e.target.value)}
                  placeholder="Título"
                  maxLength={60}
                  className="bg-black border border-white/15 px-1.5 py-1 text-[10px] text-white focus:border-amber-400/50 focus:outline-none rounded"
                  data-testid={`music-track-title-${i}`}
                />
                <input
                  value={t.id}
                  onChange={(e) => updatePlaylistItem(i, "id", e.target.value)}
                  placeholder="ID o URL de YouTube"
                  className="bg-black border border-white/15 px-1.5 py-1 text-[9px] font-mono text-gray-300 focus:border-amber-400/50 focus:outline-none rounded"
                  data-testid={`music-track-url-${i}`}
                />
              </div>
              <button onClick={() => savePlaylistItem(i)} className="text-[9px] px-1.5 py-1 bg-amber-400/80 text-black font-bold uppercase rounded hover:bg-amber-400" data-testid={`music-track-save-${i}`}>OK</button>
            </div>
          ))}
          <p className="text-[9px] text-gray-500 leading-tight pt-1">Pega URL completa o ID de 11 caracteres. Cambios visibles solo en este navegador.</p>
        </div>
      )}

      {/* Player */}
      {playing ? (
        <iframe
          ref={iframeRef}
          width="100%"
          height="80"
          src={`https://www.youtube.com/embed/${videoId}?autoplay=1&loop=1&playlist=${videoId}&controls=1&rel=0&modestbranding=1`}
          title="YouTube music player"
          frameBorder="0"
          allow="autoplay; encrypted-media"
          allowFullScreen
          data-testid="music-iframe"
        />
      ) : (
        <div className="p-3 text-center">
          <button onClick={() => setPlaying(true)} className="bg-[#CCFF00] hover:bg-[#B3E600] text-black text-xs px-3 py-1.5 rounded font-bold uppercase" data-testid="music-play-btn">
            <MusicNote size={12} weight="fill" className="inline mr-1" /> Reproducir
          </button>
        </div>
      )}
    </div>
  );
}
